import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import type { RootState } from '../../store';
import { usePreferences } from '../../contexts/PreferencesContext';
import { ROLE_LABELS, rolesOfUser, type Role } from '../../types/practiceArea';
import { getPersonaConfig } from '../../utils/personaConfig';
import { enterRole, ROLE_LENS, type InAppRole } from '../../utils/activeRole';

/**
 * Route-level notices that replace SILENT redirects to the dashboard (QA Q54,
 * Q29). A lawyer who follows a stale link or opens another workspace's tool
 * must be told what happened — landing on "/" looked like the app ignored
 * the click.
 */

const panel = 'flex flex-col items-center justify-center min-h-[40vh] text-center px-4';
const primaryBtn =
  'px-3 py-1.5 text-sm font-medium bg-brass text-chamber-ink hover:bg-brass-hover disabled:opacity-60 transition-colors';
const secondaryLink =
  'px-3 py-1.5 text-sm font-medium text-brass-ink hover:bg-parchment-hover transition-colors';

export const NotFoundPage: React.FC = () => {
  const { pathname } = useLocation();
  return (
    <div className={panel} role="alert">
      <p className="font-display text-xl text-chamber-panel mb-1">Page not found</p>
      <p className="text-sm text-parchment-muted-warm mb-4">
        There is no page at <span className="font-plex">{pathname}</span>. The link may be old or mistyped.
      </p>
      <Link to="/" className={primaryBtn}>Go to the dashboard</Link>
    </div>
  );
};

/** Nav label for a path — read from the single nav source, never retyped. */
const labelForPath = (pathname: string): string | null => {
  const config = getPersonaConfig('lawyer');
  const item = [...config.navItems, ...config.toolItems].find((i) => i.path === pathname);
  return item?.label ?? null;
};

/**
 * Shown by RoleRoute when this tab's workspace cannot use the page. Offers a
 * switch into the first allowed in-app workspace the lawyer actually holds;
 * otherwise explains why there is none. Same switch as the matter page's
 * WrongWorkspaceNotice (enterRole + lens + reload).
 */
export const RoleGateNotice: React.FC<{ allowed: Role[] }> = ({ allowed }) => {
  const { pathname } = useLocation();
  const profile = useSelector((s: RootState) => s.auth.user);
  const { setActivePracticeArea } = usePreferences();
  const [switching, setSwitching] = useState(false);

  const held = rolesOfUser(profile?.practice_areas);
  const target = allowed.find(
    (r): r is InAppRole => (r === 'criminal' || r === 'civil') && held.has(r),
  );
  const toolName = labelForPath(pathname) ?? 'This page';
  const workspaceName = allowed.map((r) => ROLE_LABELS[r]).join(' or ');

  const switchAndOpen = async () => {
    if (!target) return;
    setSwitching(true);
    enterRole(target);
    try {
      await setActivePracticeArea(ROLE_LENS[target]);
    } catch {
      /* the lens also persists to localStorage optimistically */
    }
    window.location.reload();
  };

  return (
    <div className={panel} role="alert">
      <p className="text-sm text-chamber-panel mb-1">
        {toolName} is a {workspaceName} tool.
      </p>
      <p className="text-xs text-parchment-muted-warm mb-4">
        {target
          ? 'This tab is open in a different workspace.'
          : 'That workspace is not on your profile — add it in Settings to use this tool.'}
      </p>
      <div className="flex gap-2">
        {target && (
          <button type="button" disabled={switching} onClick={() => void switchAndOpen()} className={primaryBtn}>
            {switching ? 'Switching…' : `Switch to ${ROLE_LABELS[target]}`}
          </button>
        )}
        <Link to="/" className={secondaryLink}>Back to dashboard</Link>
      </div>
    </div>
  );
};
